import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Eye,
  CheckCheck,
  Sparkles,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import {
  BETA_LESSON_COUNT,
  excludedNonBetaLessonTitles,
  betaModules,
} from "@/lib/launch/launchExperienceRoster";

interface CaptionRow {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  video_url: string | null;
  has_video: boolean;
  beta_order: number | null;
  status: "missing" | "draft" | "needs_review" | "approved" | "failed";
  source: string | null;
  instructor: string | null;
  cue_count: number;
  generated_at: string | null;
  reviewed_at: string | null;
  error_message: string | null;
  asset_changed: boolean;
}

const STATUS_COLOR: Record<string, string> = {
  approved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  needs_review: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  draft: "bg-muted text-muted-foreground border-border",
  missing: "bg-muted text-muted-foreground border-border",
  failed: "bg-red-500/20 text-red-300 border-red-500/40",
};

export default function AdminLessonCaptions() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [rows, setRows] = useState<CaptionRow[]>([]);
  const [previewRow, setPreviewRow] = useState<CaptionRow | null>(null);
  const [previewCues, setPreviewCues] = useState<Array<{ startTime: number; endTime: number; text: string }>>([]);
  const [previewTranscript, setPreviewTranscript] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-lesson-captions", {
        body: { action: "status" },
      });
      if (error) throw error;
      setRows((data).rows ?? []);
    } catch (e: unknown) {
      toast({ title: "Failed to load captions", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const generateOne = useCallback(async (lessonId: string, force = false) => {
    setBusy(lessonId);
    try {
      const { data, error } = await supabase.functions.invoke("generate-lesson-captions", {
        body: { action: "generate", lesson_id: lessonId, force },
      });
      if (error) throw error;
      const status = (data)?.status;
      if (status === "failed") {
        toast({ title: "Generation failed", description: (data)?.error ?? "Unknown error", variant: "destructive" });
      } else {
        toast({ title: "Captions generated", description: `Status: ${status}` });
      }
      await load();
    } catch (e: unknown) {
      toast({ title: "Generate failed", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }, [load, toast]);

  const generateMissingBeta = useCallback(async () => {
    setBusy("__missing_beta__");
    try {
      const { data, error } = await supabase.functions.invoke("generate-lesson-captions", {
        body: { action: "generate_missing_beta" },
      });
      if (error) throw error;
      const summary = (data)?.summary;
      const desc = summary
        ? `${summary.total_beta_lessons} beta lessons · ${summary.generated} generated · ${summary.skipped} cached · ${summary.failed} failed · ${summary.no_video} without video`
        : "Done";
      toast({
        title: summary?.failed ? `Done with ${summary.failed} failures` : "Beta captions generated",
        description: desc,
        variant: summary?.failed ? "destructive" : "default",
      });
      await load();
    } catch (e: unknown) {
      toast({ title: "Bulk generate failed", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }, [load, toast]);

  const approve = useCallback(async (lessonId: string) => {
    setBusy(lessonId);
    try {
      const { error } = await supabase.functions.invoke("generate-lesson-captions", {
        body: { action: "approve", lesson_id: lessonId },
      });
      if (error) throw error;
      toast({ title: "Captions approved" });
      await load();
    } catch (e: unknown) {
      toast({ title: "Approve failed", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }, [load, toast]);

  const openPreview = useCallback(async (row: CaptionRow) => {
    setPreviewRow(row);
    setPreviewCues([]);
    setPreviewTranscript(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-lesson-captions", {
        body: { action: "preview", lesson_id: row.lesson_id },
      });
      if (error) throw error;
      const cap = (data)?.caption;
      setPreviewCues(Array.isArray(cap?.cues) ? cap.cues : []);
      setPreviewTranscript(cap?.fallback_transcript ?? null);
    } catch (e: unknown) {
      toast({ title: "Preview failed", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    }
  }, [toast]);

  const summary = useMemo(() => {
    const total = rows.length;
    const withVideo = rows.filter((r) => r.has_video);
    const noVideo = total - withVideo.length;
    const approved = withVideo.filter((r) => r.status === "approved").length;
    const needsReview = withVideo.filter((r) => r.status === "needs_review").length;
    const failed = withVideo.filter((r) => r.status === "failed").length;
    const missing = withVideo.filter((r) => r.status === "missing").length;
    const ready = withVideo.length > 0 && withVideo.every((r) => r.status === "needs_review" || r.status === "approved");
    const expected = BETA_LESSON_COUNT;
    const generated = withVideo.filter((r) => r.cue_count > 0).length;
    return { ready, total, withVideo: withVideo.length, noVideo, approved, needsReview, failed, missing, expected, generated };
  }, [rows]);

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold">Lesson Video Captions</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Auto-transcribe lesson videos with ElevenLabs Scribe, review for accuracy, then approve.
            Captions are saved once and reused for every learner.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading || !!busy}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={generateMissingBeta} disabled={!!busy}>
            {busy === "__missing_beta__" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate Missing Captions for All Beta Lesson Videos
          </Button>
        </div>
      </div>

      <Card className={summary.ready ? "border-emerald-500/40" : "border-amber-500/40"}>
        <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {summary.ready ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            )}
            <span className="font-semibold">
              {summary.ready ? "Beta caption-ready" : "Beta NOT caption-ready"}
            </span>
          </div>
          <div className="flex gap-3 text-sm">
            <span><strong>{summary.expected}</strong> beta lessons expected</span>
            <span>{summary.total} caption rows available</span>
            <span>{summary.generated} generated</span>
            <span className="text-emerald-300">{summary.approved} approved</span>
            <span className="text-amber-300">{summary.needsReview} needs review</span>
            <span className="text-muted-foreground">{summary.missing} missing</span>
            <span className="text-red-300">{summary.failed} failed</span>
            {summary.noVideo > 0 && <span className="text-muted-foreground">{summary.noVideo} no video</span>}
            <span className="text-muted-foreground">0 non-beta lessons included</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardContent className="py-3 text-xs text-muted-foreground">
          Excluded non-beta lessons (do not receive lesson-video captions):
          {" "}{excludedNonBetaLessonTitles.join(" · ")}.
          The beta modules ({betaModules.join(", ")}) use the separate
          instructor-voice caption system, not lesson-video captions.
        </CardContent>
      </Card>

      {loading && !rows.length && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          <Card key={r.lesson_id}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                {r.beta_order != null && (
                  <Badge variant="outline" className="font-mono">#{r.beta_order}</Badge>
                )}
                <span className="font-heading">{r.lesson_name}</span>
                <Badge variant="outline" className={STATUS_COLOR[r.status] ?? ""}>{r.status}</Badge>
                {!r.has_video && (
                  <Badge variant="outline" className="bg-muted text-muted-foreground">no video</Badge>
                )}
                {r.asset_changed && (
                  <Badge variant="outline" className="bg-orange-500/20 text-orange-300 border-orange-500/40">
                    video changed
                  </Badge>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {r.course_name} · <span className="font-mono">{r.lesson_id.slice(0, 8)}</span>
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground text-xs">Video</div>
                  <div>{r.has_video ? "✓ uploaded" : "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Cues</div>
                  <div>{r.cue_count}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Source</div>
                  <div className="truncate">{r.source ?? "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Reviewed</div>
                  <div>{r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString() : "—"}</div>
                </div>
              </div>
              {r.error_message && (
                <p className="text-xs text-red-300">Error: {r.error_message}</p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={!r.has_video || !!busy}
                  onClick={() => generateOne(r.lesson_id, false)}
                >
                  {busy === r.lesson_id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                  {r.status === "missing" || r.status === "failed" ? "Generate" : "Re-check"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!r.has_video || !!busy}
                  onClick={() => generateOne(r.lesson_id, true)}
                >
                  Regenerate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={r.status === "missing"}
                  onClick={() => openPreview(r)}
                >
                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                  Preview
                </Button>
                {r.status === "needs_review" && (
                  <Button
                    size="sm"
                    variant="default"
                    disabled={!!busy}
                    onClick={() => approve(r.lesson_id)}
                  >
                    <CheckCheck className="w-3.5 h-3.5 mr-1.5" />
                    Approve
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!previewRow} onOpenChange={(o) => !o && setPreviewRow(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Caption preview — {previewRow?.lesson_name}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto space-y-2 pr-2">
            {previewCues.length > 0 ? (
              previewCues.map((c, i) => (
                <div key={i} className="text-sm border-l-2 border-primary/40 pl-3">
                  <div className="text-xs text-muted-foreground font-mono">
                    {c.startTime.toFixed(2)}s — {c.endTime.toFixed(2)}s
                  </div>
                  <div>{c.text}</div>
                </div>
              ))
            ) : previewTranscript ? (
              <p className="text-sm whitespace-pre-wrap">{previewTranscript}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No caption content yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}